import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { financialRecordService } from "../services/service/financialRecordService";

const EMPTY_PARAMS = {};
const TRANSACTIONS_KEY = ["transactions"];

export const useTransactions = (shopId, initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();

    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        search: searchTerm || undefined,
        category:
            categoryFilter !== "all"
                ? categoryFilter
                : undefined,
        type:
            typeFilter !== "all"
                ? typeFilter
                : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...initialParams,
    }), [
        currentPage,
        pageSize,
        searchTerm,
        categoryFilter,
        typeFilter,
        startDate,
        endDate,
        initialParams,
    ]);

    const query = useQuery({
        queryKey: [...TRANSACTIONS_KEY, "shop", shopId, params],
        queryFn: () =>
            financialRecordService.getTransactionsByShop(shopId, params),
        enabled: Boolean(shopId),
        staleTime: Infinity,
    });

    const invalidate = useCallback(() => {
        return queryClient.invalidateQueries({
            queryKey: TRANSACTIONS_KEY,
        });
    }, [queryClient]);

    const createTransaction = useCallback(
        async (data) => {
            const response = await financialRecordService.create(data);
            await invalidate();
            return response;
        },
        [invalidate]
    );

    const updateTransaction = useCallback(
        async (financialId, data) => {
            const response = await financialRecordService.update(
                financialId,
                data
            );
            await invalidate();
            return response;
        },
        [invalidate]
    );

    return {
        transactions: query.data?.data || [],
        summary: query.data?.summary || {
            totalIn: 0,
            totalOut: 0,
            netBalance: 0,
            breakdown: {},
            totalTransactions: 0,
        },
        pagination: query.data?.meta || null,

        loading: query.isLoading || query.isFetching,
        error: query.error,

        searchTerm,
        setSearchTerm,

        categoryFilter,
        setCategoryFilter,

        typeFilter,
        setTypeFilter,

        currentPage,
        setCurrentPage,

        pageSize,
        setPageSize,

        startDate,
        setStartDate,

        endDate,
        setEndDate,

        refresh: query.refetch,

        createTransaction,
        updateTransaction,
    };
};